/// <reference path="google.maps.d.ts" />

class Map
{
	/**
	 * Скрипт карт Google подключён к станице
	 */
	private static scriptCreated: boolean = false;
	/**
	 * Скрипт карт Google подключён и инициализирован
	 */
	private static scriptLoaded: boolean = false;
	/**
	 * Функции для вызова после подключения скрипта карт Google
	 */
	private static firstInitListeners: FirstInitListener[] = [];
	/**
	 * Список функций для инициализации элементов
	 */
	private static initElementCallbacks: InitElementCallback[] = [];
	/**
	 * Подключённые дополнительные модули
	 */
	private static plugins: DictionaryOf<any> = {};
	
	/**
	 * Элемент страницы с картой
	 */
	public element: HTMLElement;
	/**
	 * Объект карты
	 */
	public map: google.maps.Map;
	
	/**
	 * Установить функцию для вызова после инициализации скрипта карт Google
	 * @param {FirstInitListener} callback
	 */
	public static addFirstInitListener( callback: FirstInitListener ): void
	{
		if ( Map.scriptLoaded )
		{
			callback();
		}
		
		Map.firstInitListeners.push( callback );
	}
	
	/**
	 * Зарегистрировать новый дополнительный модуль
	 * @param {Function} name
	 * @param {string} initializer
	 */
	public static registerPlugin( name: string, initializer: () => any ): void
	{
		Map.addFirstInitListener(
			() => Map.plugins[name] = initializer()
		);
	}
	
	/**
	 * Запросить дополнительный модуль
	 * @param {string} name
	 * @returns {T}
	 */
	public static plugin<T>( name: string, ...rest: any[] ): T
	{
		return Map.plugins.hasOwnProperty( name )
		? (
			typeof Map.plugins[name] === 'function'
			? new Map.plugins[name]( ...rest )
			: Map.plugins[name]
		)
		: null;
	}
	
	/**
	 * Инициализировать все зарегистрированные элементы карт
	 */
	private static initAllElements(): void
	{
		Map.initElementCallbacks.forEach(
			( callback: InitElementCallback ) => callback()
		);
	}
	
	/**
	 * Действия после инициализации скрипта карт Google
	 */
	private static onScriptLoaded(): void
	{
		Map.scriptLoaded = true;
		
		Map.firstInitListeners.forEach(
			( listener: FirstInitListener ) => {
				if ( typeof listener === 'function' )
				{
					listener();
				}
			}
		);
	}
	
	/**
	 * Подключить к странице скрипт карт Google
	 */
	private static createMapsScript(): void
	{
		var script: HTMLScriptElement;
		var callbackName: string;
		const lang = document.documentElement.lang as string;
		
		Map.scriptCreated = true;
		callbackName = 'initializeMap' + Date.now();
		(<any>window)[callbackName] = Map.initAllElements;
		script = document.createElement( 'script' );
		script.type = 'text/javascript';
		script.src = `http://maps.googleapis.com/maps/api/js?key=AIzaSyAlo1hlVPWhF_BRdi0ar_BFmE1iVWk3O2o&v=3&language=${lang}&callback=${callbackName}`;
		document.head.appendChild( script );
	}
	
	/**
	 * Элемент с картой
	 * @param {HTMLElement} element Элемент страницы, в который будет помещена карта
	 * @param {MapOptionsCallback?} options Функция, возвращающая параметры карты (опционально)
	 * @param {MapInitCallback?} callback Действия после инициализации (опционально)
	 */
	public constructor( element: HTMLElement, options?: MapOptionsCallback, callback?: MapInitCallback )
	{
		if (
			Map.scriptLoaded
			|| (
				( typeof google !== 'undefined' )
				&& google.maps
				&& ( typeof google.maps.Map === 'function' )
			)
		)
		{
			this.initElement( element, options, callback );
		}
		else
		{
			Map.initElementCallbacks.push(
				this.initElement.bind( this, element, options, callback )
			);
			
			if ( !Map.scriptCreated )
			{
				Map.createMapsScript();
			}
		}
	}
	
	/**
	 * Изменить размер карты
	 * Если не переданы размеры, сработает как обновление карты под текущие.
	 * @param {number?} width Новая ширина (опционально)
	 * @param {number?} height Новая высота (опционально)
	 */
	public resize( width?: number, height?: number ): void
	{
		var element: HTMLElement;
		
		element = this.element;
		
		if ( element && element.style )
		{
			if ( width > 0 )
			{
				element.style.width = (width | 0) + 'px';
			}
			
			if ( height > 0 )
			{
				element.style.width = (height | 0) + 'px';
			}
		}
		
		if ( this.map )
		{
			google.maps.event.trigger( this.map, 'resize' );
		}
	}
	
	/**
	 * Инициализация элемента
	 * @param element
	 * @param options
	 * @param callback
	 */
	private initElement( element: HTMLElement, options: MapOptionsCallback, callback?: MapInitCallback ): void
	{
		var googleMapOptions: google.maps.MapOptions;
		
		if ( !Map.scriptLoaded )
		{
			Map.onScriptLoaded();
		}
		
		if ( typeof options === 'function' )
		{
			googleMapOptions = options();
		}
		
		this.map = new google.maps.Map( element, googleMapOptions );
		this.element = element;
		
		if ( typeof callback === 'function' )
		{
			callback( this );
		}
	}
}

interface DictionaryOf<T>
{
	[index: string]: T;
}

type FirstInitListener = () => void;
type InitElementCallback = () => void;
type MapInitCallback = ( googleMap: Map ) => void;
type MapOptionsCallback = () => google.maps.MapOptions;

export {Map as default, MapOptionsCallback};
